import { Autocomplete, FormControlLabel, Grid, TextField } from '@mui/material';

import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { FormError } from 'app/components/FormError/FormError';
import { LabeledInput } from 'app/components/RichList/items/CompanyItem/components/LabeledInput';
import { translations } from 'locales/i18n';
import { Company } from 'types/AppTypes';
import { countries, CountryType, countryToFlag, euCountries } from 'utils/Countries';

interface Props {
  company: Company;
  editMode?: boolean;
}

const CompanyInvoice = ({ company, editMode }: Props) => {
  const { t } = useTranslation();
  const formik = useFormikContext<Company>();

  const readOnlyFields = () => {
    return (
      <div className="section">
        <div className="section__header">
          {t(translations.pages.manageCompanies.invoiceAddress())}
        </div>
        <Grid container className="section__row">
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.taxId_short())}>
              {company.taxId}
            </LabeledInput>
          </Grid>
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.taxNumber())}>
              {company.taxNumber}
            </LabeledInput>
          </Grid>
        </Grid>
        <Grid container className="section__row__single">
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.invoiceEmail())}>
              {company.invoiceEmail}
            </LabeledInput>
          </Grid>
        </Grid>
        <Grid container className="section__row">
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.companyName())}>
              {company.sameInvoiceaddress ? company.name : company.invoiceName}
            </LabeledInput>
          </Grid>
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.street())}>
              {company.sameInvoiceaddress ? company.street : company.invoiceStreet}
            </LabeledInput>
          </Grid>
        </Grid>
        <Grid container className="section__row">
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.zip())}>
              {company.sameInvoiceaddress ? company.zip : company.invoiceZip}
            </LabeledInput>
          </Grid>
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.city())}>
              {company.sameInvoiceaddress ? company.city : company.invoiceCity}
            </LabeledInput>
          </Grid>
        </Grid>
        <Grid container className="section__row__single">
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.country())}>
              {company.sameInvoiceaddress ? company.country : company.invoiceCountry}
            </LabeledInput>
          </Grid>
        </Grid>
      </div>
    );
  };

  if (!editMode) {
    return readOnlyFields();
  }
  const { errors, values, setFieldValue, validateForm } = formik;

  return (
    <div className="section">
      <div className="section__header">
        {t(translations.pages.manageCompanies.invoiceAddress())}
      </div>
      <Grid container className="section__row">
        <Grid item xs>
          <Field
            as={TextField}
            name="taxId"
            error={errors.taxId}
            helperText={errors.taxId}
            label={t(translations.common.taxId_short())}
            required={
              (euCountries.includes(values.country) && values.country !== 'Germany') ||
              (values.country === 'Germany' && values.taxNumber === '')
            }
          />
        </Grid>
        <Grid item xs>
          <Field
            as={TextField}
            name="taxNumber"
            error={errors.taxNumber}
            helperText={errors.taxNumber}
            label={t(translations.common.taxNumber())}
            disabled={values.country !== 'Germany'}
            required={values.country === 'Germany' && values.taxId === ''}
          />
        </Grid>
      </Grid>
      <Grid container className="section__row__single">
        <Grid item xs>
          <Field
            as={TextField}
            name="invoiceEmail"
            error={errors.invoiceEmail}
            helperText={errors.invoiceEmail}
            label={t(translations.common.invoiceEmail())}
            required
          />
        </Grid>
      </Grid>
      <Grid container className="section__row">
        <Grid item xs>
          <Field
            as={TextField}
            name="invoiceName"
            error={errors.invoiceName}
            helperText={errors.invoiceName}
            required={!values.sameInvoiceaddress}
            label={t(translations.common.companyName())}
            value={values.sameInvoiceaddress ? values.name : values.invoiceName}
            disabled={values.sameInvoiceaddress}
          />
        </Grid>
        <Grid item xs>
          <Field
            as={TextField}
            name="invoiceStreet"
            error={errors.invoiceStreet}
            helperText={errors.invoiceStreet}
            required={!values.sameInvoiceaddress}
            label={t(translations.common.street())}
            value={values.sameInvoiceaddress ? values.street : values.invoiceStreet}
            disabled={values.sameInvoiceaddress}
          />
        </Grid>
      </Grid>
      <Grid container className="section__row">
        <Grid item xs>
          <Field
            as={TextField}
            name="invoiceZip"
            error={errors.invoiceZip}
            helperText={errors.invoiceZip}
            required={!values.sameInvoiceaddress}
            label={t(translations.common.zip())}
            value={values.sameInvoiceaddress ? values.zip : values.invoiceZip}
            disabled={values.sameInvoiceaddress}
          />
        </Grid>
        <Grid item xs>
          <Field
            as={TextField}
            name="invoiceCity"
            error={errors.invoiceCity}
            helperText={errors.invoiceCity}
            required={!values.sameInvoiceaddress}
            label={t(translations.common.city())}
            value={values.sameInvoiceaddress ? values.city : values.invoiceCity}
            disabled={values.sameInvoiceaddress}
          />
        </Grid>
      </Grid>
      <Grid container className="section__row__single">
        <Grid item xs>
          <FormControlLabel
            className="Mui-error Mui-required"
            control={
              <Autocomplete
                options={countries as CountryType[]}
                autoHighlight
                fullWidth
                disabled={values.sameInvoiceaddress}
                value={
                  values.sameInvoiceaddress
                    ? countries.find(c => c.label === values.country)
                      ? countries.find(c => c.label === values.country)
                      : null
                    : countries.find(c => c.label === values.invoiceCountry)
                    ? countries.find(c => c.label === values.invoiceCountry)
                    : null
                }
                getOptionLabel={option => option.label}
                renderOption={(props, option) => (
                  <li {...props}>
                    <span style={{ marginRight: '0.5em' }}>{countryToFlag(option.code)}</span>
                    {option.label} ({option.code})
                  </li>
                )}
                onChange={async (event, value, reason) => {
                  await setFieldValue('invoiceCountry', value?.label, false);
                  await validateForm();
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t(translations.common.chooseACountry())}
                    inputProps={{
                      ...params.inputProps,
                      style: { padding: '6 0 7' },
                      autoComplete: 'new-password'
                    }}
                  />
                )}
              />
            }
            label={''}
            style={{ width: '100%', marginBottom: '0.5em' }}
          />
          {errors.invoiceCountry && <FormError message={errors.invoiceCountry} />}
        </Grid>
      </Grid>
    </div>
  );
};
export default CompanyInvoice;
